import React, { useEffect, useRef, useState } from "react";
import "../styles/ContactForm.css";

import ContactPattern from "../assets/contact-pattern1.jpg";
import ContactGirl from "../assets/contact-girl.png";

const WHATSAPP_NUMBER = "917358798182";

const ContactForm = () => {
  const colLeftRef = useRef(null);
  const girlRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});

  /* ── Reveal girl image reliably on scroll AND on refresh ──
     Observe the column wrapper (no transform), toggle class on the image */
  useEffect(() => {
    const wrapEl = colLeftRef.current;
    const imgEl = girlRef.current;
    if (!wrapEl || !imgEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            imgEl.classList.add("cf-girl--visible");
            observer.disconnect();
          }
        });
      },
      { threshold: 0 }
    );

    observer.observe(wrapEl);

    return () => observer.disconnect();
  }, []);

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (/^[A-Za-z\s]*$/.test(value)) {
      setFormData((prev) => ({ ...prev, name: value }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    if (/^\d{0,10}$/.test(value)) {
      setFormData((prev) => ({ ...prev, phone: value }));
    }
  };

  const handleEmailChange = (e) => {
    setFormData((prev) => ({ ...prev, email: e.target.value }));
  };

  const handleSubjectChange = (e) => {
    setFormData((prev) => ({ ...prev, subject: e.target.value }));
  };

  const handleMessageChange = (e) => {
    setFormData((prev) => ({ ...prev, message: e.target.value }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Full name is required.";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required.";
    } else if (formData.phone.length !== 10) {
      newErrors.phone = "Enter a valid 10-digit phone number.";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Enter a valid email address.";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required.";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required.";
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const text =
      `*New Contact Form Submission*\n\n` +
      `*Full Name:* ${formData.name}\n` +
      `*Phone Number:* ${formData.phone}\n` +
      `*Email Address:* ${formData.email}\n` +
      `*Subject:* ${formData.subject}\n` +
      `*Message:* ${formData.message}`;

    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
      text
    )}`;

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");

    setFormData({ name: "", phone: "", email: "", subject: "", message: "" });
    setErrors({});
  };

  return (
    <section className="cf-section">

      {/* BG — pattern image only, no overlay */}
      <img src={ContactPattern} alt="TanBio BioMed Genomics Laboratory in Thiruvarur" loading="lazy" className="cf-bg" aria-hidden="true" />

      <div className="cf-inner">
        <div className="row g-0 cf-row">

          {/* ── LEFT: narrow col, girl fills full height ── */}
          <div ref={colLeftRef} className="col-lg-5 col-md-4 d-none d-md-flex cf-col-left">
            <img
              ref={girlRef}
              src={ContactGirl}
              alt="Lab technician"
              loading="lazy"
              className="cf-girl-img"
            />
          </div>

          {/* ── RIGHT: wide col, form ── */}
          <div className="col-lg-7 col-md-8 col-12 cf-col-right">

            <h2 className="cf-title">Contact Us</h2>
            <p className="cf-subtitle">
              Have a question or need to book a service? We're here to help.
            </p>

            {/* Mobile only: girl between title and form */}
            <div className="d-flex d-md-none justify-content-center cf-girl-mobile-wrap">
              <img src={ContactGirl} alt="Lab technician" loading="lazy" className="cf-girl-img-mobile" />
            </div>

            <form onSubmit={handleSubmit} noValidate>

              {/* Row 1: Name + Phone */}
              <div className="row g-3 mb-3">
                <div className="col-sm-6 col-12">
                  <div className={`cf-field ${errors.name ? "cf-field--invalid" : ""}`}>
                    <input
                      type="text"
                      className="cf-input"
                      placeholder="Full Name*"
                      value={formData.name}
                      onChange={handleNameChange}
                    />
                    <i className="fa-regular fa-user cf-icon"></i>
                  </div>
                  {errors.name && <div className="cf-error">{errors.name}</div>}
                </div>
                <div className="col-sm-6 col-12">
                  <div className={`cf-field ${errors.phone ? "cf-field--invalid" : ""}`}>
                    <input
                      type="tel"
                      inputMode="numeric"
                      className="cf-input"
                      placeholder="Phone Number*"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                    />
                    <i className="fa-regular fa-phone cf-icon"></i>
                  </div>
                  {errors.phone && <div className="cf-error">{errors.phone}</div>}
                </div>
              </div>

              {/* Row 2: Email + Subject */}
              <div className="row g-3 mb-3">
                <div className="col-sm-6 col-12">
                  <div className={`cf-field ${errors.email ? "cf-field--invalid" : ""}`}>
                    <input
                      type="email"
                      className="cf-input"
                      placeholder="Email Address*"
                      value={formData.email}
                      onChange={handleEmailChange}
                    />
                    <i className="fa-regular fa-envelope cf-icon"></i>
                  </div>
                  {errors.email && <div className="cf-error">{errors.email}</div>}
                </div>
                <div className="col-sm-6 col-12">
                  <div className={`cf-field ${errors.subject ? "cf-field--invalid" : ""}`}>
                    <input
                      type="text"
                      className="cf-input"
                      placeholder="Subject*"
                      value={formData.subject}
                      onChange={handleSubjectChange}
                    />
                    <i className="fa-regular fa-tag cf-icon"></i>
                  </div>
                  {errors.subject && <div className="cf-error">{errors.subject}</div>}
                </div>
              </div>

              {/* Row 3: Message */}
              <div className="row g-3 mb-3">
                <div className="col-12">
                  <div className={`cf-field cf-field--ta ${errors.message ? "cf-field--invalid" : ""}`}>
                    <textarea
                      className="cf-input cf-textarea"
                      placeholder="Your Message*"
                      rows="4"
                      value={formData.message}
                      onChange={handleMessageChange}
                    ></textarea>
                    <i className="fa-regular fa-message cf-icon cf-icon--top"></i>
                  </div>
                  {errors.message && <div className="cf-error">{errors.message}</div>}
                </div>
              </div>

              {/* Button — centered */}
              <div className="d-flex justify-content-center mt-2">
                <button type="submit" className="cf-btn">
                  Send via WhatsApp <i className="fa-brands fa-whatsapp ms-2"></i>
                </button>
              </div>

            </form>
          </div>

        </div>
      </div>
    </section>
  );
};

export default ContactForm;